import { healthResponseSchema, type HealthResponse } from "@brainics/api-contract";
import { useQuery } from "@tanstack/react-query";

import { navigate } from "../lib/routing.js";

async function fetchHealth(): Promise<HealthResponse> {
  const response = await fetch("/api/health", {
    headers: { accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`API health request failed with status ${response.status}`);
  }

  return healthResponseSchema.parse(await response.json());
}

const audiences = [
  {
    title: "Заказчикам",
    text: "Создавайте вакансии и получайте ранжированный список подходящих специалистов.",
  },
  {
    title: "Подрядчикам",
    text: "Управляйте базой специалистов, доступностью, откликами и загрузкой на проектах.",
  },
  {
    title: "Специалистам",
    text: "Соберите структурированный профиль и находите релевантные проектные предложения.",
  },
];

export function LandingPage({ authenticated }: { authenticated: boolean }) {
  const healthQuery = useQuery({
    queryKey: ["api-health"],
    queryFn: fetchHealth,
    refetchInterval: 60_000,
  });

  const healthLabel = healthQuery.isPending
    ? "Проверяем API"
    : healthQuery.isSuccess
      ? "API доступен"
      : "API недоступен";
  const entryLabel = authenticated ? "В кабинет" : "Войти";
  const openEntry = () => navigate(authenticated ? "/app" : "/login");

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-white/10 bg-slate-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <a className="text-xl font-semibold tracking-tight" href="/">
            Brainics
          </a>
          <nav className="hidden items-center gap-7 text-sm text-slate-300 md:flex">
            <a className="transition hover:text-white" href="#platform">
              Платформа
            </a>
            <a className="transition hover:text-white" href="#audiences">
              Возможности
            </a>
            <a className="transition hover:text-white" href="#status">
              Статус
            </a>
          </nav>
          <button
            className="rounded-full border border-white/20 px-5 py-2 text-sm font-medium transition hover:border-white/40 hover:bg-white/5"
            onClick={openEntry}
            type="button"
          >
            {entryLabel}
          </button>
        </div>
      </header>

      <main>
        <section className="mx-auto grid max-w-7xl gap-12 px-6 py-24 lg:grid-cols-[1.2fr_0.8fr] lg:items-center lg:py-32">
          <div>
            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
              Human Cloud Platform
            </p>
            <h1 className="max-w-4xl text-5xl font-semibold leading-tight tracking-tight sm:text-6xl">
              IT-специалисты и проектные команды в едином рабочем процессе
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300">
              Brainics объединяет профили специалистов, вакансии, объяснимый мэтчинг,
              воронку подбора, назначения и учёт рабочего времени.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <button
                className="rounded-full bg-cyan-300 px-6 py-3 font-semibold text-slate-950 transition hover:bg-cyan-200"
                onClick={openEntry}
                type="button"
              >
                Найти специалиста
              </button>
              <button
                className="rounded-full border border-white/20 px-6 py-3 font-semibold transition hover:border-white/40 hover:bg-white/5"
                onClick={openEntry}
                type="button"
              >
                Предоставить специалистов
              </button>
            </div>
          </div>

          <aside
            className="rounded-3xl border border-white/10 bg-white/[0.04] p-7 shadow-2xl shadow-cyan-950/30"
            id="status"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-slate-400">Platform status</p>
                <p className="mt-1 text-xl font-semibold">Brainics MVP</p>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  healthQuery.isSuccess
                    ? "bg-emerald-400/15 text-emerald-300"
                    : healthQuery.isError
                      ? "bg-rose-400/15 text-rose-300"
                      : "bg-amber-400/15 text-amber-300"
                }`}
              >
                {healthLabel}
              </span>
            </div>
            <dl className="mt-8 space-y-4 text-sm">
              <div className="flex justify-between gap-4 border-b border-white/10 pb-4">
                <dt className="text-slate-400">Frontend</dt>
                <dd className="font-medium">React + Vite</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-white/10 pb-4">
                <dt className="text-slate-400">Backend</dt>
                <dd className="font-medium">Express + TypeScript</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-white/10 pb-4">
                <dt className="text-slate-400">Database</dt>
                <dd className="font-medium">PostgreSQL + Drizzle</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-400">Текущая фаза</dt>
                <dd className="font-medium">Companies</dd>
              </div>
            </dl>
          </aside>
        </section>

        <section className="border-y border-white/10 bg-white/[0.025]" id="platform">
          <div className="mx-auto max-w-7xl px-6 py-16">
            <p className="max-w-4xl text-2xl leading-10 text-slate-200">
              Основной сценарий MVP: заказчик создаёт вакансию, система подбирает
              специалистов, кандидат проходит воронку, выходит на проект, а часы фиксируются
              и согласовываются внутри платформы.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-20" id="audiences">
          <div className="mb-10 max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">
              Единое окно
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
              Три стороны — один прозрачный процесс
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {audiences.map((audience) => (
              <article
                className="rounded-2xl border border-white/10 bg-white/[0.035] p-6"
                key={audience.title}
              >
                <h3 className="text-xl font-semibold">{audience.title}</h3>
                <p className="mt-3 leading-7 text-slate-400">{audience.text}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-6 py-8 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 Brainics</p>
          <p>Human Cloud Platform</p>
        </div>
      </footer>
    </div>
  );
}
